import React from "react";
import { useNavigate } from "react-router-dom";
import { useNexauraData } from "../context/NexauraContext";
import { useWallet } from "../context/WalletContext";
import { useToast } from "../components/common/ToastContext";
import Card from "../components/common/Card";
import Button from "../components/common/Button";
import StatCounter from "../components/common/StatCounter";
import DonutChart from "../components/common/DonutChart";
import RingProgress from "../components/common/RingProgress";
import Avatar from "../components/common/Avatar";
import MiniBars from "../components/common/MiniBars";
import Skeleton, { SkeletonRows } from "../components/common/Skeleton";
import { fmtUsdt, toNumber, fmtNumber, shortAddr, boardState, boardPct } from "../utils/format";

const SEG_META = [
  { key: "direct", label: "Direct", color: "#ffc01e" },
  { key: "upgrade", label: "Upgrade", color: "#2ea8ff" },
  { key: "level", label: "Level", color: "#1e73ff" },
  { key: "royalty", label: "Royalty", color: "#a78bfa" },
  { key: "gold", label: "Gold", color: "#ffd45e" },
  { key: "diamond", label: "Diamond", color: "#22d3ee" },
  { key: "global", label: "Global", color: "#2ee6a6" },
];

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

function getRank(data) {
  const hb = data.highestBoard;
  const e = data.eligibility;
  if (e.global || hb >= 9) return { name: "Global", cls: "r-global" };
  if (e.diamond || hb >= 6) return { name: "Diamond", cls: "r-diamond" };
  if (e.gold || hb >= 3) return { name: "Gold", cls: "r-gold" };
  if (hb >= 1) return { name: "Starter", cls: "r-starter" };
  return { name: "Guest", cls: "r-starter" };
}

export default function Dashboard() {
  const { data, busy, withdraw, claimAll, upgrade, triggerAutoUpgrade } = useNexauraData();
  const { account } = useWallet();
  const navigate = useNavigate();
  const toast = useToast();

  const d = data.details;
  const b = data.breakdown;
  const claimable = d ? toNumber(d.claimableAmount) : 0;
  const minW = data.minWithdraw ? toNumber(data.minWithdraw) : 0;
  const canWithdraw = claimable > 0 && claimable >= minW;

  const handleWithdraw = () => {
    if (!canWithdraw) {
      toast.warning("Minimum not reached", `You need at least ${minW} USDT to withdraw.`);
      return;
    }
    withdraw();
  };
  const current = data.highestBoard;
  const nextBoard = current < 12 ? current + 1 : 0;
  const nextPrice = nextBoard ? data.prices[nextBoard - 1] : null;
  const hold = current ? data.held[current - 1] : null;
  const rank = getRank(data);

  // Overall ceiling progress across active boards (finite caps only).
  let earned = 0, cap = 0;
  data.packageDetails.forEach((det) => {
    if (det && det.isActive) {
      const c = toNumber(det.ceilingLimit);
      if (c > 0 && c < 1e15) { earned += toNumber(det.totalEarned); cap += c; }
    }
  });
  const overallPct = cap > 0 ? Math.min(100, (earned / cap) * 100) : 0;

  // 7-day income trend from activity events.
  const incomeActs = (data.activities || []).filter((a) => a.type === "income" && a.ts);
  const now = new Date();
  const trend = [];
  for (let i = 6; i >= 0; i--) {
    const day = new Date(now); day.setHours(0, 0, 0, 0); day.setDate(day.getDate() - i);
    const start = day.getTime(); const end = start + 86400000;
    const sum = incomeActs.filter((a) => a.ts >= start && a.ts < end).reduce((s, a) => s + toNumber(a.amount), 0);
    trend.push({ label: DAY_LABELS[day.getDay()], value: sum });
  }

  const refLink = `${window.location.origin}${window.location.pathname}?ref=${data.userId}`;
  const copy = () => {
    try { navigator.clipboard.writeText(refLink); toast.success("Copied", "Referral link copied."); }
    catch { toast.error("Copy failed", "Long-press the link to copy."); }
  };

  const segments = b ? SEG_META.map((s) => ({ label: s.label, color: s.color, value: toNumber(b[s.key]) })) : [];
  const breakdownTotal = segments.reduce((s, x) => s + x.value, 0);

  const recent = (data.activities || []).slice(0, 8);

  if (!data.registered) {
    return (
      <div className="screen">
        <Card glow="gold" className="section-gap" style={{ marginTop: 24 }}>
          <div className="card-title">Not registered yet</div>
          <p className="muted" style={{ fontSize: 13.5, margin: "4px 0 16px" }}>
            This wallet doesn't have a Nexaura account. Register to activate Board 1 and start earning.
          </p>
          <Button onClick={() => navigate("/register")}>Go to Registration</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="screen">
      {/* ── USER + RANK ─────────────────────────────── */}
      <div className="user-head">
        <div className="user-head-glow" />
        <div className="uh-top">
          <Avatar address={account} id={data.userId} size={52} />
          <div className="uh-id">
            <div className="uh-name">User #{data.userId}</div>
            <div className="uh-addr">{shortAddr(account)}</div>
          </div>
          <div className={`rank-badge ${rank.cls}`}>
            <span className="rb-shine" />
            {rank.name}
          </div>
        </div>
        <div className="uh-row" style={{ marginTop: 14 }}>
          <div className="uh-item"><span className="uh-k">Upline</span><span className="uh-v">#{d ? d.referrer : "—"}</span></div>
          <div className="uh-item"><span className="uh-k">Package</span><span className="uh-v gradient-text">Board {current || "—"}</span></div>
          <div className="uh-item"><span className="uh-k">Directs</span><span className="uh-v">{d ? d.totalDirectReferrals : 0}</span></div>
        </div>
      </div>

      {/* ── HERO: balance + ceiling ring ─────────────── */}
      <div className="hero-ring">
        <RingProgress value={overallPct} size={196} stroke={13} variant={overallPct > 80 ? "gold" : "blue"}>
          <div className="hr-label">Available</div>
          <div className="hr-value"><StatCounter value={claimable} /></div>
          <div className="hr-unit">USDT</div>
          <div className="hr-ceil">{overallPct.toFixed(0)}% ceiling</div>
        </RingProgress>
      </div>
      {minW > 0 && (
        <div className="min-w-hint">Minimum withdrawal: <b>{minW} USDT</b></div>
      )}

      {/* ── QUICK ACTIONS ────────────────────────────── */}
      <div className="quick-actions">
        <button className="qa" onClick={handleWithdraw} disabled={busy}><span className="qa-ico">↑</span>Withdraw</button>
        <button className="qa" onClick={claimAll} disabled={busy}><span className="qa-ico">✦</span>Claim All</button>
        <button className="qa" onClick={() => nextBoard && upgrade(nextBoard)} disabled={busy || !nextBoard}><span className="qa-ico">⤴</span>Upgrade</button>
        <button className="qa" onClick={copy}><span className="qa-ico">⧉</span>Invite</button>
      </div>

      {/* ── TOTAL INCOME + TREND ─────────────────────── */}
      <div className="income-highlight">
        <div className="ih-shine" />
        <div className="ih-label">Total Income Earned</div>
        <div className="ih-value"><StatCounter value={d ? toNumber(d.totalIncome) : 0} /> <span>USDT</span></div>
        <div className="ih-trend">
          {data.activitiesLoaded ? (
            <MiniBars data={trend} height={56} />
          ) : (
            <Skeleton w="100%" h={64} r={10} />
          )}
        </div>
        <div className="ih-trend-cap">Last 7 days</div>
      </div>

      {/* ── TEAM + LEVELS ────────────────────────────── */}
      <div className="stat-grid">
        <div className="stat"><div className="s-label">Total Team</div><div className="s-value">{fmtNumber(data.referrals.length)}</div></div>
        <div className="stat"><div className="s-label">Total Claimed</div><div className="s-value">{d ? fmtUsdt(d.totalClaimed) : "0"}</div></div>
      </div>

      <Card title="Levels Unlocked" className="section-gap">
        <div className="flex-between" style={{ marginBottom: 8 }}>
          <span className="muted" style={{ fontSize: 12.5 }}>20 generations total</span>
          <span style={{ fontWeight: 800 }}>{data.levelsUnlocked}/20</span>
        </div>
        <div className="level-pips">
          {Array.from({ length: 20 }, (_, i) => (
            <span key={i} className={`pip ${i < data.levelsUnlocked ? "on" : ""}`} />
          ))}
        </div>
      </Card>

      {/* ── ALL PACKAGES ─────────────────────────────── */}
      <Card title="All Packages" className="section-gap">
        <div className="pkg-grid">
          {Array.from({ length: 12 }, (_, i) => {
            const det = data.packageDetails[i];
            const st = boardState(det);
            const pct = boardPct(det);
            return (
              <div className={`pkg-cell ${st}`} key={i} title={`Board ${i + 1}`}>
                <div className="pkg-no">B{i + 1}</div>
                <div className="pkg-bar"><i style={{ width: `${pct}%` }} /></div>
                <div className="pkg-pct">{det && det.isActive ? `${pct.toFixed(0)}%` : "—"}</div>
              </div>
            );
          })}
        </div>
        <div className="pkg-legend">
          <span><i className="d live" />Live</span>
          <span><i className="d warn" />Nearing</span>
          <span><i className="d full" />Ceiling</span>
          <span><i className="d locked" />Inactive</span>
        </div>
      </Card>

      {/* ── INCOME BREAKDOWN ─────────────────────────── */}
      <Card title="Income Breakdown" className="section-gap">
        <div className="donut-wrap">
          <DonutChart segments={segments} centerTop={<b>{fmtUsdt(b ? b.totalIncome : 0)}</b>} centerSub="USDT" />
          <div className="legend">
            {SEG_META.map((s) => {
              const val = b ? toNumber(b[s.key]) : 0;
              const pct = breakdownTotal > 0 ? (val / breakdownTotal) * 100 : 0;
              return (
                <div className="legend-row" key={s.key}>
                  <span className="dot" style={{ background: s.color, boxShadow: `0 0 8px ${s.color}` }} />
                  <span className="lg-label">{s.label}</span>
                  <span className="lg-val">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* ── NEXT BOARD + HOLD ────────────────────────── */}
      <Card glow="blue" className="section-gap">
        <div className="card-title">Next Board</div>
        {nextBoard ? (
          <>
            <div className="flex-between" style={{ marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Board {nextBoard}</div>
                <div className="muted" style={{ fontSize: 12.5 }}>{nextPrice ? fmtUsdt(nextPrice) : "—"} USDT to activate</div>
              </div>
              <Button size="sm" loading={busy} onClick={() => upgrade(nextBoard)}>Upgrade →</Button>
            </div>
            {!data.heldLoaded ? (
              <Skeleton w="100%" h={60} r={12} />
            ) : hold ? (
              <div className="hold-box">
                <div className="flex-between">
                  <span className="muted" style={{ fontSize: 12.5 }}>Auto-Upgrade Hold</span>
                  <span style={{ fontWeight: 800 }}>{hold.heldCount}/5</span>
                </div>
                <div className="hold-bar">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span key={i} className={`hb ${i < hold.heldCount ? "on" : ""}`} />
                  ))}
                </div>
                <div className="flex-between" style={{ marginTop: 8 }}>
                  <span className="muted" style={{ fontSize: 12 }}>Held amount</span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{fmtUsdt(hold.heldAmount)} USDT</span>
                </div>
                {hold.pending && (
                  <Button size="sm" variant="gold" loading={busy} onClick={() => triggerAutoUpgrade(current)}>
                    Trigger Auto-Upgrade
                  </Button>
                )}
              </div>
            ) : null}
          </>
        ) : (
          <div className="muted" style={{ fontSize: 13.5 }}>🎉 All 12 boards active — you're at the top!</div>
        )}
      </Card>

      {/* ── ACTIVITIES ───────────────────────────────── */}
      <Card title="Recent Activity">
        {!data.activitiesLoaded ? (
          <SkeletonRows rows={4} />
        ) : recent.length === 0 ? (
          <div className="empty" style={{ padding: "24px 10px" }}>No recent activity yet.</div>
        ) : (
          recent.map((a) => (
            <div className="activity" key={a.key}>
              <span className={`act-ico ${a.type}`}>{a.type === "withdraw" ? "↑" : a.type === "upgrade" ? "⤴" : "+"}</span>
              <div className="act-body">
                <div className="act-label">{a.label}</div>
                {a.board ? <div className="act-sub">Board {a.board}</div> : null}
              </div>
              <span className={`act-amt ${a.type === "withdraw" ? "out" : "in"}`}>
                {a.type === "withdraw" ? "−" : "+"}{fmtUsdt(a.amount)}
              </span>
            </div>
          ))
        )}
      </Card>

      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <Button variant="secondary" onClick={() => navigate("/boards")}>Boards</Button>
        <Button variant="secondary" onClick={() => navigate("/team")}>Team</Button>
      </div>
    </div>
  );
}
